import { forwardRef } from 'react';
import { classNames } from 'tradera/utils/class-names';

import type { ButtonProps } from '../atoms/button/button';
import { Button } from '../atoms/button/button';
import ArrowDownIcon from '@tradera/blueprint/theme/icons2/arrow-down.svg?jsx';
import DropdownIconSvg from '@tradera/blueprint/theme/icons2/dropdown.svg?jsx';

import styles from './dropdown-button.module.scss';

// trying to make this a bit restrictive...since if you want a Button, use that instead
export type DropdownButtonProps = Omit<ButtonProps, 'theme' | 'href'> & {
    isOpen?: boolean;
    iconClassName?: string;
    theme?: 'dropdown' | 'unbutton';
    id?: string;
    /**
     * This will use the thinner DropdownIcon SVG instead of the heavier ArrowDownIcon SVG
     */
    thinIcon?: boolean;
};

/**
 * Just a button with a specific look and feel used for dropdowns.
 *
 * Pass `isOpen` to change the icon to an open state.
 */
export const DropdownButton = forwardRef<
    React.ElementRef<typeof Button>,
    DropdownButtonProps
>(function DropdownButton(
    {
        children,
        className,
        fluid,
        isOpen,
        iconClassName,
        theme = 'dropdown',
        nonBrandedText,
        thinIcon,
        ...props
    },
    forwardedRef
) {
    return (
        <Button
            theme={theme}
            className={classNames(className)}
            ref={forwardedRef}
            iconRight={
                thinIcon ? (
                    <DropdownIconSvg
                        className={classNames(
                            'icon',
                            iconClassName,
                            isOpen && styles.iconOpen
                        )}
                    />
                ) : (
                    <DropdownIcon className={iconClassName} isOpen={isOpen} />
                )
            }
            // If we would always use `fixed` here, the icon would be on top of the text since it's absolutely positioned when fixed
            fixedIcon={fluid}
            align="left"
            fluid={fluid}
            nonBrandedText={nonBrandedText}
            {...props}>
            {children}
        </Button>
    );
});

type DropdownIconProps = { isOpen?: boolean; className?: string };
export const DropdownIcon = ({ isOpen, className }: DropdownIconProps) => (
    <ArrowDownIcon
        className={classNames(className, 'icon', {
            [styles.iconOpen]: isOpen
        })}
    />
);
